import type { PublicGallery } from '@/modules/gallery';
import type { GalleryDetail } from '@/modules/gallery/domain/entities/GalleryDetail';
import type { PublicProfile } from '@/types';
import { getSiteSettings } from '@/modules/seo/siteSettings';

export type ImageKind = 'hero-decorative' | 'gallery-cover' | 'gallery-preview' | 'profile-avatar' | 'profile-header';

type BuildAltInput = {
  title?: string | null;
  displayName?: string | null;
  explicitAlt?: string | null;
};

export type ImageCandidateInput = {
  src?: string | null;
  thumbnailSrc?: string | null;
  webpSrc?: string | null;
  width?: number | null;
  height?: number | null;
  alt?: string | null;
};

export type ImageCandidate = {
  src: string;
  thumbnailSrc?: string;
  webpSrc?: string;
  width?: number;
  height?: number;
  alt: string;
};

export type ImageSizePreset = 'gallery-card' | 'featured-gallery' | 'gallery-hero' | 'profile-avatar';

type SocialImageEntity =
  | { kind: 'home'; origin: string }
  | { kind: 'gallery'; origin: string; gallery: GalleryDetail | null }
  | { kind: 'profile'; origin: string; profile: PublicProfile | null };

export function buildAltText(kind: ImageKind, input: BuildAltInput): string {
  const explicitAlt = input.explicitAlt?.trim();

  if (kind === 'hero-decorative' || kind === 'profile-header') {
    return '';
  }

  if (kind === 'gallery-cover') {
    return input.title?.trim() || '';
  }

  if (kind === 'gallery-preview') {
    if (explicitAlt) return explicitAlt;
    return input.title?.trim() ? `Preview de ${input.title.trim()}` : 'Preview de galería';
  }

  return input.displayName?.trim() || '';
}

export function buildImageCandidate(input: ImageCandidateInput): ImageCandidate | null {
  const src = input.src?.trim();
  if (!src) return null;

  return {
    src,
    thumbnailSrc: input.thumbnailSrc?.trim() || undefined,
    webpSrc: input.webpSrc?.trim() || undefined,
    width: typeof input.width === 'number' ? input.width : undefined,
    height: typeof input.height === 'number' ? input.height : undefined,
    alt: input.alt?.trim() ?? '',
  };
}

export function buildSizes(preset: ImageSizePreset): string {
  switch (preset) {
    case 'gallery-card':
      return '(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw';
    case 'featured-gallery':
      return '(max-width: 768px) 100vw, 33vw';
    case 'gallery-hero':
      return '(max-width: 1024px) 100vw, 60vw';
    case 'profile-avatar':
      return '128px';
  }
}

function absoluteUrl(origin: string, pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return pathOrUrl.startsWith('/') ? `${origin}${pathOrUrl}` : `${origin}/${pathOrUrl}`;
}

function resolveConfiguredSocialImagePath(): string | undefined {
  const configuredPath = getSiteSettings().seo_default_social_image_path.trim();
  return configuredPath !== '' ? configuredPath : undefined;
}

export function selectSocialImage(entity: SocialImageEntity): string | undefined {
  const fallbackPath = resolveConfiguredSocialImagePath();

  if (entity.kind === 'home') {
    return fallbackPath ? absoluteUrl(entity.origin, fallbackPath) : undefined;
  }

  if (entity.kind === 'gallery') {
    const galleryImage = entity.gallery?.previewImages[0]?.webpUrl
      ?? entity.gallery?.previewImages[0]?.thumbnailUrl
      ?? entity.gallery?.previewImages[0]?.url;
    if (galleryImage) {
      return absoluteUrl(entity.origin, galleryImage);
    }
    return fallbackPath ? absoluteUrl(entity.origin, fallbackPath) : undefined;
  }

  const profileImage = entity.profile?.avatar_url ?? entity.profile?.header_image_url;
  if (profileImage) {
    return absoluteUrl(entity.origin, profileImage);
  }
  return fallbackPath ? absoluteUrl(entity.origin, fallbackPath) : undefined;
}

export function galleryCoverCandidate(gallery: PublicGallery): ImageCandidate | null {
  return buildImageCandidate({
    src: gallery.coverImage,
    alt: buildAltText('gallery-cover', { title: gallery.title }),
  });
}
